import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Alert,
  Link,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { getCurrentOrganization, getOrganizationId } from '../utils/organizationContext';
import provinces from '../data/provinces';

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { organizationId, organizationSlug } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    address_line1: '',
    address_line2: '',
    city: '',
    province: '',
    postal_code: '',
    gender: '',
    languages: '',
    service_unit: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentOrgId, setCurrentOrgId] = useState(null);
  const [currentOrgName, setCurrentOrgName] = useState('');
  const [currentOrgSlug, setCurrentOrgSlug] = useState(null);
  const [availableOrganizations, setAvailableOrganizations] = useState([]);

  // Load organization from URL
  useEffect(() => {
    const loadOrganization = async () => {
      const slug = getCurrentOrganization();
      
      if (slug) {
        setCurrentOrgSlug(slug);
        setError(''); // Clear any previous errors
        
        // Always fetch from organizations API to ensure we have the latest data
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/organizations`
          );
          const org = response.data.organizations?.find(o => o.slug === slug && o.is_active);
          
          if (org) {
            setCurrentOrgId(org.id);
            setCurrentOrgName(org.name);
          } else {
            setError(`Organization "${slug}" not found or inactive. Please check the URL.`);
            setCurrentOrgId(null);
          }
        } catch (err) {
          console.error('Error fetching organization:', err);
          setError('Failed to load organization. Please try again.');
          setCurrentOrgId(null);
        }
      } else {
        // If no organization in URL, fetch all organizations for selection
        try {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/organizations`
          );
          if (response.data.organizations && response.data.organizations.length > 0) {
            const activeOrgs = response.data.organizations.filter(org => org.is_active);
            setAvailableOrganizations(activeOrgs);
            
            // If only one organization, auto-select it
            if (activeOrgs.length === 1) {
              const org = activeOrgs[0];
              setCurrentOrgId(org.id);
              setCurrentOrgSlug(org.slug);
              setCurrentOrgName(org.name);
            }
          }
        } catch (err) {
          console.error('Error fetching organizations:', err);
          setError('Failed to load organizations. Please try again.');
        }
      }
    };
    loadOrganization();
  }, [organizationId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate organization
    if (!currentOrgId) {
      if (availableOrganizations.length > 1) {
        setError('Please select a church branch to continue.');
      } else {
        setError('Unable to determine organization. Please ensure you are accessing the correct URL.');
      }
      return;
    }
    
    // Basic validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Filter out empty fields and confirmPassword, add organization_id
    const submitData = Object.fromEntries(
      Object.entries(formData).filter(([key, value]) => 
        value !== '' && key !== 'confirmPassword'
      )
    );
    
    // Add organization_id from URL context
    submitData.organization_id = currentOrgId;

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/auth/signup`,
        submitData
      );
      
      setSuccess(response.data.message);
      setTimeout(() => {
        // Navigate to organization-prefixed signup success page
        const successPath = currentOrgSlug ? `/${currentOrgSlug}/signup-success` : '/signup-success';
        navigate(successPath);
      }, 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Signup failed. Please try again.');
    }
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} sx={{ p: 4, mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Create User Account
        </Typography>
        
        {currentOrgName && (
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Chip 
              label={`Signing up for: ${currentOrgName}`} 
              color="primary" 
              variant="outlined"
            />
          </Box>
        )}

        {!currentOrgId && availableOrganizations.length > 1 && (
          <FormControl fullWidth margin="normal" required>
            <InputLabel>Select Church Branch</InputLabel>
            <Select
              value={currentOrgId || ''}
              label="Select Church Branch"
              onChange={(e) => {
                const selectedOrg = availableOrganizations.find(org => org.id === e.target.value);
                if (selectedOrg) {
                  setCurrentOrgId(selectedOrg.id);
                  setCurrentOrgSlug(selectedOrg.slug);
                  setCurrentOrgName(selectedOrg.name);
                }
              }}
            >
              {availableOrganizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                required
              />
            </Grid>
          </Grid>

          <TextField
            fullWidth
            margin="normal"
            label="Email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />

          <TextField
            fullWidth
            margin="normal"
            label="Phone Number"
            value={formData.phone_number}
            onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
            required
          />

          <TextField
            fullWidth
            margin="normal"
            label="Password"
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required
          />

          <TextField
            fullWidth
            margin="normal"
            label="Confirm Password"
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
            error={formData.password !== formData.confirmPassword && formData.confirmPassword !== ''}
            helperText={
              formData.password !== formData.confirmPassword && formData.confirmPassword !== '' 
                ? 'Passwords do not match' 
                : ''
            }
            required
          />

          <TextField
            fullWidth
            margin="normal"
            label="Address Line 1"
            value={formData.address_line1}
            onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
          />
          
          <TextField
            fullWidth
            margin="normal"
            label="Address Line 2 (Optional)"
            value={formData.address_line2}
            onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
          />
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Province</InputLabel>
                <Select
                  value={formData.province}
                  label="Province"
                  onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                >
                  {provinces.map((province) => (
                    <MenuItem key={province.code} value={province.name}>
                      {province.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Postal Code"
                value={formData.postal_code}
                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
              />
            </Grid>
          </Grid>

          <FormControl component="fieldset" sx={{ mt: 2 }}>
            <FormLabel>Gender</FormLabel>
            <RadioGroup
              row
              value={formData.gender}
              onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
            >
              <FormControlLabel value="male" control={<Radio />} label="Male" />
              <FormControlLabel value="female" control={<Radio />} label="Female" />
            </RadioGroup>
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            label="Languages Spoken"
            placeholder="e.g., English, French"
            value={formData.languages}
            onChange={(e) => setFormData({ ...formData, languages: e.target.value })}
          />

          <FormControl fullWidth margin="normal">
            <FormLabel>Service Unit</FormLabel>
            <RadioGroup
              row
              value={formData.service_unit}
              onChange={(e) => setFormData({ ...formData, service_unit: e.target.value })}
            >
              <FormControlLabel value="pastorate" control={<Radio />} label="Pastorate" />
              <FormControlLabel value="hospitality" control={<Radio />} label="Hospitality" />
              <FormControlLabel value="follow_up" control={<Radio />} label="Follow Up" />
              <FormControlLabel value="homecell" control={<Radio />} label="Homecell" />
              <FormControlLabel value="nehemiah_squad" control={<Radio />} label="Nehemiah Squad" />
              <FormControlLabel value="call_center" control={<Radio />} label="Call Center" />
            </RadioGroup>
          </FormControl>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            sx={{ mt: 3 }}
          >
            Sign Up
          </Button>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link href="/login" underline="hover">
              Already have an account? Login
            </Link>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Signup; 